import { Divider } from 'components/commons/Divider'
import { Link } from 'components/commons/Link'
import Page from 'components/Page'
import { useTranslations } from 'next-intl'
import { Children, isValidElement, ReactElement, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

type DocPageProps = {
  group: string
  title: string
  metadata: { title: string, description: string }
  next?: { name: string, to: string }
  previous?: { name: string, to: string }
  children: ReactNode
}

type ElementWithChildren = ReactElement<{ children?: ReactNode, className?: string }>

const hasChildrenProp = (node: ReactNode): node is ElementWithChildren =>
  isValidElement(node)

const DIACRITICS_PATTERN = new RegExp('[̀-ͯ]', 'g')

const slugify = (text: string) =>
  text
    .normalize('NFD')
    .replace(DIACRITICS_PATTERN, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const getNodeText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join('')
  if (hasChildrenProp(node)) return getNodeText(node.props.children)
  return ''
}

const DocPage = ({ group, title, metadata, next, previous, children }: DocPageProps) => {

  const t = useTranslations('layout/features')

  const usedSlugs = new Set<string>()
  const headings: { id: string, text: string }[] = []

  const content = Children.map(children, child => {
    if (!hasChildrenProp(child) || child.type !== 'h2') return child

    const text = getNodeText(child.props.children)
    let id = slugify(text) || `topic-${headings.length + 1}`
    while (usedSlugs.has(id)) {
      id = `${id}-${usedSlugs.size + 1}`
    }
    usedSlugs.add(id)
    headings.push({ id, text })

    return (
      <h2
        {...child.props}
        id={id}
        className={twMerge('scroll-mt-28 md:scroll-mt-36', child.props.className)}
      />
    )
  })

  return (
    <Page title={metadata.title} description={metadata.description} className='md:pt-32 lg:pl-12'>
      <div>
        <div className={twMerge(headings.length > 0 && 'xl:grid xl:grid-cols-[1fr_240px] xl:gap-10')}>
          <div className={twMerge(headings.length > 0 && 'max-w-3xl')}>
            <label className='text-blue text-md font-bold'>{group}</label>
            <h1 id='top' className='text-4xl font-semibold mt-4 scroll-mt-28 md:scroll-mt-36'>{title}</h1>
            {content}
          </div>
          {headings.length > 0 &&
            <nav className='hidden xl:block sticky top-32 self-start max-h-[calc(100vh-8rem)] overflow-y-auto'>
              <ul className='space-y-2 border-l pl-4 border-gray-800'>
                <li>
                  <a href='#top' className='font-extrabold text-black text-xs leading-tight hover:text-blue'>{title}</a>
                </li>
                {headings.map(({ id, text }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className='text-xs leading-tight text-gray-900 font-semibold hover:text-blue'
                    >
                      {text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          }
        </div>
        <Divider variant='secondary' className='mt-8 mb-4' />
        <div className='grid gap-4 md:grid-cols-2'>
          <div>
            {previous &&
              <div className='text-left md:text-left'>
                <div >
                  <label className='font-extrabold text-sm'>{t("previous")}</label>
                </div>
                <Link
                  href={previous.to}
                  className='hover:text-blue'
                >
                  <label className='text-md font-extrabold tracking-wider ml-2 '>{'< '} {previous.name}</label>
                </Link>
              </div>
            }
          </div>
          <div>
            {next &&
              <div className='text-right'>
                <div>
                  <label className='font-extrabold text-sm'>{t("next")}</label>
                </div>
                <Link href={next.to}>
                  <label className='text-md font-extrabold tracking-wider mr-2 hover:text-blue'>{next.name} {' >'} </label>
                </Link>
              </div>
            }
          </div>
        </div>
      </div>
    </Page >
  )
}

export default DocPage
